import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Group, Stack, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import {
  getGetCategoriesOptionallyfilteredbylocationQueryKey,
  useUpdateCategory,
} from "@/shared/api/generated/categories";
import { GetCategoriesItem } from "@/shared/api/generated/schemas";

const updateCategorySchema = z.object({
  name: z.string().min(1),
});

type UpdateCategoryFormValues = z.infer<typeof updateCategorySchema>;

export default function UpdateCategoryForm({
  category,
  close,
}: {
  category: GetCategoriesItem;
  close: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCategory } = useUpdateCategory({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getGetCategoriesOptionallyfilteredbylocationQueryKey(),
        }),
    },
  });

  const form = useForm<UpdateCategoryFormValues>({
    mode: "controlled",
    validate: zodResolver(updateCategorySchema),
    initialValues: {
      name: category.name,
    },
  });

  async function handleSubmit(values: UpdateCategoryFormValues) {
    await updateCategory(
      {
        id: category.id,
        data: {
          name: values.name,
          locationId: category.location.id,
        },
      },
      {
        onSuccess: close,
      },
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label={t`Navn`}
          placeholder={t`Velg navn`}
          withAsterisk
          {...form.getInputProps("name")}
        />

        <Group justify="end" gap={8} mt={8}>
          <Button variant="subtle" onClick={close}>
            <Trans>Avbryt</Trans>
          </Button>
          <Button type="submit">
            <Trans>Opprett</Trans>
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
